/* eslint-disable jsx-a11y/click-events-have-key-events */
import { forwardRef, ComponentPropsWithoutRef } from 'react';
import { Warehouse } from '@views/WarehouseDetails/WarehouseDetails';
import { Text } from '@components/Text';
import { WAREHOUSE_IMAGES_URL } from '@constants';
import {
  MdLocationOn,
  MdBusiness,
  MdInventory,
  MdWarning,
  MdEditNote,
} from 'react-icons/md';
import {
  StyledWarehouseList,
  StyledWarehouseLoading,
  StyledWarehouseLoadingIndicator,
} from './WarehouseList.styles';

interface WarehouseListProps extends ComponentPropsWithoutRef<'div'> {
  id?: string;
}

export const WarehouseList = forwardRef<HTMLDivElement, WarehouseListProps>(
  ({ children, id = 'warehouse-list' }, forwardedRef) => {
    return (
      <StyledWarehouseList id={id} ref={forwardedRef}>
        {children}
      </StyledWarehouseList>
    );
  }
);

WarehouseList.displayName = 'WarehouseList';

interface WarehouseCardProps extends ComponentPropsWithoutRef<'div'> {
  warehouseSummary: Warehouse;
  orientation: 'vertical' | 'horizontal';
  onClick: () => void;
}

const warehouseImagePlaceholder = `/images/warehouse_details_placeholder.png`;

export const WarehouseCard = forwardRef<HTMLDivElement, WarehouseCardProps>(
  ({ warehouseSummary, orientation, onClick, ...props }, forwardedRef) => {
    const hasActualImage =
      warehouseSummary.images && warehouseSummary.images.length > 0;
    const imageUrl = hasActualImage
      ? `${WAREHOUSE_IMAGES_URL}/${warehouseSummary.images[0].imageLocation}/small/${warehouseSummary.images[0].remoteName}`
      : warehouseImagePlaceholder;

    const isDraft = warehouseSummary.isDraft;

    const hasFailedVetting =
      warehouseSummary.authenticity &&
      warehouseSummary.authenticity.authenticityLevel !== 'High' &&
      warehouseSummary.authenticity.authenticityLevel !== 'Very High' &&
      !warehouseSummary.authenticity.wasSuccessfulAudit;

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className='group relative flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-gray-300 w-[220px]'
        onClick={onClick}
        {...props}
        ref={forwardedRef}
      >
        {/* Draft Badge */}
        {isDraft && (
          <div className='absolute top-2 right-2 z-10 flex items-center gap-1 bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full'>
            <MdEditNote className='w-3.5 h-3.5' />
            <span>Draft</span>
          </div>
        )}

        {/* Failed Vetting Badge */}
        {!isDraft && hasFailedVetting && (
          <div className='absolute top-2 right-2 z-10 flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-medium px-2 py-1 rounded-full'>
            <MdWarning className='w-3.5 h-3.5' />
            <span>Quarantined</span>
          </div>
        )}

        {/* Image container */}
        <div className='relative h-[140px] bg-gray-50'>
          <img
            src={imageUrl}
            alt={warehouseSummary.name}
            className={`w-full h-full ${
              hasActualImage ? 'object-cover' : 'object-contain p-4'
            } ${isDraft ? 'opacity-60' : ''}`}
          />
        </div>

        {/* Info section */}
        <div className='flex flex-col gap-1.5 p-3 border-t border-gray-100'>
          <h3 className='text-base font-semibold text-gray-900 truncate'>
            {warehouseSummary.name}
          </h3>
          <div className='flex items-center gap-1.5 text-sm text-gray-500'>
            <MdLocationOn className='w-4 h-4 flex-shrink-0' />
            <span className='truncate'>{warehouseSummary.address}</span>
          </div>
          <div className='flex items-center gap-1.5 text-sm text-gray-500'>
            <MdBusiness className='w-4 h-4 flex-shrink-0' />
            <span className='truncate'>
              {warehouseSummary.businessName ||
                warehouseSummary.userBusinessName}
            </span>
          </div>
          <div className='flex items-center gap-1.5 text-sm text-gray-500'>
            <MdInventory className='w-4 h-4 flex-shrink-0' />
            <span>
              {warehouseSummary.currentlyAvailableCapacity}{' '}
              {warehouseSummary.storageCapacityUnitOfMeasure}
            </span>
          </div>
        </div>
      </div>
    );
  }
);

WarehouseCard.displayName = 'WarehouseCard';

export function WarehouseLoading() {
  return (
    <StyledWarehouseLoading>
      <StyledWarehouseLoadingIndicator />
      <Text
        size='xl'
        weight='bold'
        css={{ color: 'inherit', textAlign: 'center' }}
      >
        Fetching Warehouse details...
      </Text>
    </StyledWarehouseLoading>
  );
}

WarehouseLoading.displayName = 'WarehouseLoading';
